import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import AddTagsDialog from "../../dialog/tags-dialog";
export const TagBadge = ({ tag }) => {
  return (
    <Badge variant="secondary" className="text-xs mr-1 ml-1">
      #{tag}
    </Badge>
  );
};

const CollapsibleTagList = ({ service, tags = [], availableWidth }) => {
  const [visibleTagCount, setVisibleTagCount] = useState(0);
  const measuringRef = useRef(null);
  const rootRef = useRef(null);
  const [tagToMeasure, setTagToMeasure] = useState(null); // State to control rendering of TagBadge
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (measuringRef.current) {
      rootRef.current = createRoot(measuringRef.current);
    }

    return () => {
      if (rootRef.current) {
        rootRef.current.unmount();
      }
    };
  }, []);

  useEffect(() => {
    const calculateVisibleTags = async () => {
      if (!rootRef.current || !tags || tags.length === 0) {
        setVisibleTagCount(0);
        return;
      }
      let runningWidth = 0;
      let count = 0;

      for (const tag of tags) {
        setTagToMeasure(tag); // Set the tag to be measured

        // Wait for the next render cycle to get the updated measurement
        await new Promise((resolve) => setTimeout(resolve, 0));

        const tagWidth = measuringRef.current.getBoundingClientRect().width;

        if (runningWidth + tagWidth + 4 > availableWidth) {
          break;
        }

        runningWidth += tagWidth + 4;
        count++;
      }

      setTagToMeasure(null); // Reset the tag to measure
      setVisibleTagCount(count);
    };

    calculateVisibleTags();
  }, [tags, rootRef, availableWidth]);

  return tags.length === 0 ? (
    <div className="w-full flex items-center justify-between  h-full">
      <span className="text-secondary">No tags</span>
      <AddTagsDialog
        service={service}
        tags={tags}
        open={isOpen}
        setOpen={setIsOpen}
      />
    </div>
  ) : (
    <>
      <div className="flex items-center h-full p-2">
        {tags.slice(0, visibleTagCount).map((tag) => (
          <TagBadge key={tag} tag={tag} />
        ))}
        {tags.length > visibleTagCount && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <span className="text-xs mr-1 ml-1 underline cursor-pointer">
                +{tags.length - visibleTagCount} more
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {tags.slice(visibleTagCount).map((tag) => (
                <DropdownMenuItem key={tag}>
                  <TagBadge tag={tag} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div
        ref={measuringRef}
        style={{
          visibility: "hidden",
          position: "absolute",
          display: "inline-block",
        }}
      >
        {tagToMeasure && (
          <Badge variant="secondary" className="text-xs mr-1 ml-1">
            #{tagToMeasure}
          </Badge>
        )}
      </div>
    </>
  );
};

export default CollapsibleTagList;

CollapsibleTagList.propTypes = {
  serviceName: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  availableWidth: PropTypes.number.isRequired,
};
